import React, { useMemo } from 'react';
import moment from 'moment';
import DeltaArrowIcon from '../icons/DeltaArrowIcon';
import {
	CHART_PRIMARY_COLOR,
	COMPARISON_PERIOD_COLOR,
	NEGATIVE_CHANGE_COLOR,
	POSITIVE_CHANGE_COLOR,
} from './LifeCycleAnalyticsConstants';
import { buildSeriesComparisonTooltipHtml } from './LifeCycleAnalyticsUtils';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_FIELD,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	SpendBarChart,
	pairComparisonByMonth,
} from '../analytics_charts';

const SPEND_CHART_MAX_MONTHS = 6;

const SpendChart = ({
	chartColor,
	currency,
	spendChartData,
	previousPeriodChartData,
	spendSummary,
	selectedPriorityCard,
	formatSpendAxisLabel,
	buildSpendTooltip,
	handleSpendIntervalClick,
	exportSpendCsv,
	comparisonState,
	onComparisonChange,
	selectedRange,
	comparisonRange,
	comparisonLoading,
	comparisonDisabled,
	comparisonDisabledReason,
}: any) => {
	const barColor = chartColor || CHART_PRIMARY_COLOR;
	const previousColor = COMPARISON_PERIOD_COLOR;
	const comparisonMode = comparisonState ? comparisonState.mode : COMPARISON_MODE.NONE;
	const hasComparison = comparisonMode !== COMPARISON_MODE.NONE;

	const chartData = useMemo(() => {
		const sliced = (spendChartData || []).slice(-SPEND_CHART_MAX_MONTHS);
		const slicedPrev = (previousPeriodChartData || []).slice(-SPEND_CHART_MAX_MONTHS);
		const paired = pairComparisonByMonth({
			current: sliced,
			previous: slicedPrev,
			hasComparison,
			selectedRange,
			comparisonRange,
			buildPreviousRow: (prevRow, curRow: any) => ({
				...(prevRow || {}),
				previousMonthLabel: prevRow ? prevRow.monthLabel : '',
				previousMonthFull: prevRow ? prevRow.monthFull : '',
				monthLabel: curRow.monthLabel,
			}),
		});
		return [...paired.previous, ...paired.current];
	}, [spendChartData, previousPeriodChartData, hasComparison, selectedRange, comparisonRange]);

	const pairCount = useMemo(() => {
		if (!hasComparison) return 0;
		return chartData.filter(
			(row: any) => row[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT
		).length;
	}, [chartData, hasComparison]);

	// Compress categories into a fraction of the plot width when fewer pairs are shown so the
	// dodge band width stays roughly constant, keeping the intra-pair gap near 8px regardless
	// of how many months are selected.
	const monthLabelRange = useMemo<[number, number] | undefined>(() => {
		if (!hasComparison || pairCount <= 0 || pairCount >= SPEND_CHART_MAX_MONTHS) return undefined;
		const fraction = pairCount / SPEND_CHART_MAX_MONTHS;
		return [0.5 - fraction / 2, 0.5 + fraction / 2];
	}, [hasComparison, pairCount]);

	const isCurrentItem = (item: any) =>
		!!(item && item.data && item.data[COMPARISON_SERIES_FIELD] === COMPARISON_SERIES_CURRENT);

	const renderComparisonTooltip = (title: string, items: any[]) => {
		if (!items || items.length === 0) return '';
		const sortedItems = items
			.slice()
			.sort((a, b) => Number(isCurrentItem(b)) - Number(isCurrentItem(a)));
		const rows = sortedItems.map((item) => {
			const isCurrent = isCurrentItem(item);
			const labelText = isCurrent
				? item.data && (item.data.monthFull || item.data.monthLabel)
				: item.data &&
				  (item.data.previousMonthFull || item.data.previousMonthLabel || item.data.monthFull);
			return {
				label: labelText || title,
				color: isCurrent ? barColor : previousColor,
				value: currency
					? currency.format((item.data && item.data.spendValue) || 0, 0)
					: String(item.data && item.data.spendValue),
			};
		});
		return buildSeriesComparisonTooltipHtml(rows, { minWidth: 260 });
	};

	const selectedStart: moment.Moment | null = selectedRange ? selectedRange[0] : null;
	const selectedEnd: moment.Moment | null = selectedRange ? selectedRange[1] : null;
	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const headerPill = selectedPriorityCard
		? {
				label: selectedPriorityCard.woPriorityName,
				color: selectedPriorityCard.color,
				bgColor: selectedPriorityCard.bgColor,
		  }
		: null;

	const deltaElement =
		spendSummary.percentChange !== null && spendSummary.percentChange !== 0 ? (
			<div
				style={{
					color: spendSummary.percentChange > 0 ? POSITIVE_CHANGE_COLOR : NEGATIVE_CHANGE_COLOR,
				}}
				className="flex shrink-0 items-center gap-1"
			>
				<DeltaArrowIcon
					direction={spendSummary.percentChange > 0 ? 'up' : 'down'}
					color={spendSummary.percentChange > 0 ? POSITIVE_CHANGE_COLOR : NEGATIVE_CHANGE_COLOR}
				/>
				<span className="bodyregular">{Math.abs(spendSummary.percentChange)}%</span>
			</div>
		) : null;

	const title = (
		<AnalyticsChartHeader
			pill={headerPill}
			label="Spend"
			value={currency.format(spendSummary.totalSpend, 0)}
			delta={deltaElement}
		/>
	);

	const extra = (
		<AnalyticsChartActions
			comparison={
				onComparisonChange
					? {
							value: comparisonState || { mode: COMPARISON_MODE.NONE },
							onChange: onComparisonChange,
							selectedStart,
							selectedEnd,
							isTimeBased: true,
							loading: !!comparisonLoading,
							disabled: !!comparisonDisabled,
							disabledReason: comparisonDisabled ? comparisonDisabledReason : undefined,
					  }
					: undefined
			}
			onDownload={exportSpendCsv}
		/>
	);

	return (
		<AnalyticsChartCard title={title} extra={extra}>
			<div className="w-full min-w-0 print:!break-inside-avoid">
				<SpendBarChart
					data={chartData}
					hasComparison={hasComparison}
					currentColor={barColor}
					previousColor={previousColor}
					axisYTitle={`Facility Cost (${currency.id})`}
					formatYAxisLabel={formatSpendAxisLabel}
					intervalTooltipDefinition={['monthFull*spendValue*monthKey', buildSpendTooltip]}
					renderComparisonTooltip={renderComparisonTooltip}
					onIntervalClick={handleSpendIntervalClick}
					monthLabelRange={monthLabelRange}
				/>
				{hasComparison ? (
					<ComparisonLegend
						currentStart={selectedStart}
						currentEnd={selectedEnd}
						previousStart={comparisonStart}
						previousEnd={comparisonEnd}
						currentColor={barColor}
						previousColor={previousColor}
						mode={comparisonMode}
						swatchType="square"
					/>
				) : (
					<div className="mt-3 h-5" aria-hidden="true" />
				)}
			</div>
		</AnalyticsChartCard>
	);
};

export default SpendChart;
